import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useServerNotifications } from '../contexts/ServerNotificationContext';
import api from '../services/api';
import { LayoutDashboard, Users, Building2, Clock, FileText, DollarSign, UserCircle, LogOut, Monitor, Moon, Sun, Settings, Shield, CheckSquare, Trophy, Briefcase } from 'lucide-react';
import './Sidebar.css';

const Sidebar = ({ mobileOpen, onCloseMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const { getNotificationCountForType } = useServerNotifications();
  const [theme, setTheme] = useState(localStorage.getItem('portal_theme') || 'dark');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [branding, setBranding] = useState({
    logoUrl: localStorage.getItem('portalLogoDataUrl') || localStorage.getItem('portal_logo_url') || '',
    brandName: localStorage.getItem('portalBrandName') || localStorage.getItem('portal_brand_name') || 'PURA Solutions',
    brandSubtitle: localStorage.getItem('portalSubtitle') || localStorage.getItem('portal_brand_subtitle') || 'Management Portal',
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('portal_theme', theme);
  }, [theme]);

  useEffect(() => {
    const loadBranding = async () => {
      try {
        const data = await api.get('/companies/1/branding');
        const cachedLogo = localStorage.getItem('portalLogoDataUrl') || localStorage.getItem('portal_logo_url') || '';
        const cachedBrandName = localStorage.getItem('portalBrandName') || localStorage.getItem('portal_brand_name') || 'PURA Solutions';
        const cachedSubtitle = localStorage.getItem('portalSubtitle') || localStorage.getItem('portal_brand_subtitle') || 'Management Portal';

        const serverLogo = data?.customizations?.portalLogoDataUrl || data?.logoUrl || '';
        const serverBrandName = data?.customizations?.portalBrandName || data?.companyName || '';
        const serverSubtitle = data?.customizations?.portalSubtitle || '';

        const nextBranding = {
          logoUrl: serverLogo || cachedLogo,
          brandName: serverBrandName || cachedBrandName,
          brandSubtitle: serverSubtitle || cachedSubtitle,
        };
        setBranding(nextBranding);
        localStorage.setItem('portalLogoDataUrl', nextBranding.logoUrl || '');
        localStorage.setItem('portalBrandName', nextBranding.brandName || 'PURA Solutions');
        localStorage.setItem('portalSubtitle', nextBranding.brandSubtitle || 'Management Portal');
      } catch (error) {
        console.error('Error loading global branding:', error);
      }
    };

    const applyCachedBranding = () => {
      setBranding({
        logoUrl: localStorage.getItem('portalLogoDataUrl') || localStorage.getItem('portal_logo_url') || '',
        brandName: localStorage.getItem('portalBrandName') || localStorage.getItem('portal_brand_name') || 'PURA Solutions',
        brandSubtitle: localStorage.getItem('portalSubtitle') || localStorage.getItem('portal_brand_subtitle') || 'Management Portal',
      });
      loadBranding();
    };

    loadBranding();
    window.addEventListener('portal-branding-updated', applyCachedBranding);
    window.addEventListener('storage', applyCachedBranding);
    return () => {
      window.removeEventListener('portal-branding-updated', applyCachedBranding);
      window.removeEventListener('storage', applyCachedBranding);
    };
  }, []);

  useEffect(() => {
    const key = `profile_photo_${user?.userId || user?.id || 'me'}`;
    const photo = localStorage.getItem(key) || '';
    setProfilePhoto(photo);
  }, [user]);

  const handleLogout = () => {
    if (onCloseMobile) onCloseMobile();
    logout();
    navigate('/login');
  };

  const handleNavClick = (path) => {
    if (onCloseMobile) onCloseMobile();
    navigate(path);
  };

  // Calculate nav items only after permissions are loaded
  const getNavItems = () => {
    const overviewNavItems = [
      ...(hasPermission('dashboard') ? [{ path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }] : []),
    ];

    const managementNavItems = [
      ...(hasPermission('users') ? [{ path: '/users', label: 'Users', icon: Users }] : []),
      ...(hasPermission('departments') ? [{ path: '/departments', label: 'Departments', icon: Building2 }] : []),
      ...(hasPermission('attendance_management') ? [{ path: '/attendance-mgmt', label: 'Attendance', icon: Clock }] : []),
      ...(hasPermission('salary_management') ? [{ path: '/salary-mgmt', label: 'Salary', icon: DollarSign }] : []),
      ...(hasPermission('projects') ? [{ path: '/projects', label: 'Projects', icon: Briefcase }] : []),
      ...(hasPermission('rewards') ? [{ path: '/rewards', label: 'Rewards', icon: Trophy }] : []),
      ...(hasPermission('all_requests') ? [{ path: '/all-requests', label: 'All Requests', icon: FileText }] : []),
      ...(hasPermission('permissions') ? [{ path: '/permissions', label: 'Permissions', icon: Shield }] : []),
    ];

    const myRecordsNavItems = [
      { path: '/my-profile', label: 'My Profile', icon: UserCircle },
      ...(hasPermission('my_requests') ? [{ path: '/my-requests', label: 'My Requests', icon: FileText }] : []),
      ...(hasPermission('my_attendance') ? [{ path: '/my-attendance', label: 'My Attendance', icon: Clock }] : []),
      ...(hasPermission('my_salary') ? [{ path: '/my-salary', label: 'My Salary', icon: DollarSign }] : []),
      ...(hasPermission('my_tasks') ? [{ path: '/my-tasks', label: 'My Tasks', icon: CheckSquare }] : []),
      ...(hasPermission('my_rewards') ? [{ path: '/my-rewards', label: 'My Rewards', icon: Trophy }] : []),
    ];

    const remoteWorkNavItems = [
      { path: '/remote-work', label: 'Remote Work Session', icon: Monitor },
    ];

    return { overviewNavItems, managementNavItems, myRecordsNavItems, remoteWorkNavItems };
  };

  const { overviewNavItems, managementNavItems, myRecordsNavItems, remoteWorkNavItems } = getNavItems();
  const isActive = (path) => location.pathname === path;

  const pathToEntityTypes = {
    '/my-requests': ['attestation', 'leave', 'custom_request'],
    '/all-requests': ['attestation', 'leave', 'custom_request'],
    '/attendance-mgmt': ['attendance'],
    '/my-attendance': ['attendance'],
    '/projects': ['project', 'task', 'reward'],
    '/my-tasks': ['task'],
    '/rewards': ['reward'],
    '/my-rewards': ['reward'],
    '/users': ['user'],
    '/remote-work': ['attendance'],
  };

  const getBadgeCount = (path) => {
    const types = pathToEntityTypes[path] || [];
    return types.reduce((sum, t) => sum + getNotificationCountForType(t), 0);
  };

  const renderNavItem = (item, badgeCount = 0) => (
    <button
      key={item.path}
      className={isActive(item.path) ? 'active' : ''}
      onClick={() => handleNavClick(item.path)}
    >
      <item.icon size={16} />
      <span>{item.label}</span>
      {badgeCount > 0 && (
        <span className="nav-badge">{badgeCount > 99 ? '99+' : badgeCount}</span>
      )}
    </button>
  );

  return (
    <nav className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-brand">
        <div className="sidebar-logo">
          {branding.logoUrl ? <img src={branding.logoUrl} alt="Portal Logo" className="sidebar-logo-image" /> : 'PS'}
        </div>
        <div>
          <h2>{branding.brandName}</h2>
          <small>{branding.brandSubtitle}</small>
        </div>
      </div>

      <div className="sidebar-profile">
        <span className="avatar">
          {profilePhoto ? (
            <img src={profilePhoto} alt="Profile" className="avatar-image" />
          ) : (
            user?.email?.[0]?.toUpperCase() || '?'
          )}
        </span>
        <span className="info">
          <span className="name">{user?.email || '—'}</span>
          <br />
          <span className="role-label">{user?.role || '—'}</span>
        </span>
      </div>

      <div className="sidebar-nav">
        {overviewNavItems.length > 0 && (
          <>
            <div className="nav-section">Overview</div>
            {overviewNavItems.map((item) => renderNavItem(item, getBadgeCount(item.path)))}
          </>
        )}
        
        {managementNavItems.length > 0 && (
          <>
            <div className="nav-section">Management</div>
            {managementNavItems.map((item) => renderNavItem(item, getBadgeCount(item.path)))}
          </>
        )}
        
        {myRecordsNavItems.length > 0 && (
          <>
            <div className="nav-section">My Records</div>
            {myRecordsNavItems.map((item) => renderNavItem(item, getBadgeCount(item.path)))}
          </>
        )}

        {remoteWorkNavItems.length > 0 && (
          <>
            <div className="nav-section">Remote Work</div>
            {remoteWorkNavItems.map((item) => renderNavItem(item, getBadgeCount(item.path)))}
          </>
        )}
      </div>

      <div className="sidebar-footer">
        {hasPermission('settings') && (
          <button
            onClick={() => handleNavClick('/settings')}
            className={`settings-footer-btn ${isActive('/settings') ? 'active' : ''}`}
          >
            <Settings size={16} />
            Settings
          </button>
        )}
        <button onClick={() => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))} className="theme-toggle-btn">
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
        </button>
        <button onClick={handleLogout} className="logout-btn">
          <LogOut size={16} />
          Logout
        </button>
      </div>
    </nav>
  );
};

export default Sidebar;
